import { useCallback, useState } from 'react';

const PREFIJO = 'micrositio.visto.';

/**
 * Marca "esto ya lo vio" de forma persistente, por usuario y por clave.
 *
 * Lo usa la ayuda de Bajas: se abre sola la PRIMERA vez que alguien entra a la
 * sección y después no vuelve a aparecer sola. La marca va en `localStorage` y
 * no en el server a propósito: es preferencia de pantalla, no dato del negocio,
 * y no vale un endpoint ni una escritura en Mongo por cada vez que alguien
 * navega.
 *
 * La clave lleva el id del usuario: en una máquina compartida —que es el caso
 * de las cuentas `sys-*`— el segundo que entra tiene que ver la ayuda igual.
 *
 * Si `localStorage` no está disponible (modo privado, storage lleno, política
 * del navegador) se comporta como "nunca visto": la ayuda se abre siempre en vez
 * de romperse. Molesta un poco, pero nunca esconde la documentación.
 */
export function useVistoUnaVez(
  clave: string,
  usuarioId: string | undefined,
): { visto: boolean; marcarVisto: () => void } {
  const storageKey = `${PREFIJO}${usuarioId ?? 'anon'}.${clave}`;

  const [visto, setVisto] = useState<boolean>(() => {
    try {
      return globalThis.localStorage?.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  });

  const marcarVisto = useCallback(() => {
    setVisto(true);
    try {
      globalThis.localStorage?.setItem(storageKey, '1');
    } catch {
      // Sin storage la marca dura lo que dure la pantalla montada. No se
      // silencia por vagancia: no hay nada que reintentar ni que reportarle al
      // usuario, y la única consecuencia es que la ayuda vuelva a abrirse.
    }
  }, [storageKey]);

  return { visto, marcarVisto };
}
